import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CONSENT_REOPEN_EVENT,
  clearConsent,
  getMeasurementId,
  readConsent,
  writeConsent,
  type ConsentChoice,
} from '@/lib/analytics/consent'
import { loadGa4 } from '@/lib/analytics/ga'
import { enableReplayIfConsented } from '@/lib/observability/sentry'
import styles from './ConsentBanner.module.css'

/** Bottom consent banner gating optional GA4 analytics AND Sentry session
 *  recording. Renders only when GA is configured AND no choice has been made
 *  yet. Neither the Google tag nor the screen recorder loads before the visitor
 *  accepts. Mounted once at the app-shell level. */
export function ConsentBanner() {
  const measurementId = getMeasurementId()
  const [choice, setChoice] = useState<ConsentChoice | null>(() => readConsent())

  // Load the optional trackers only once consent is granted — covers both the
  // Accept click and a returning visitor who granted on a previous visit. Never
  // runs otherwise. Session recording is switched on from HERE as well as at
  // startup, so a fresh Accept takes effect without a reload; it re-reads the
  // stored choice itself, so this call cannot enable it on its own.
  useEffect(() => {
    if (choice !== 'granted') return
    if (measurementId) loadGa4(measurementId)
    enableReplayIfConsented()
  }, [measurementId, choice])

  // The footer "Cookie settings" link re-opens the banner to change a choice.
  useEffect(() => {
    function onReopen() {
      clearConsent()
      setChoice(null)
    }
    window.addEventListener(CONSENT_REOPEN_EVENT, onReopen)
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, onReopen)
  }, [])

  if (!measurementId || choice !== null) return null

  return (
    <div className={styles.banner} role="dialog" aria-label="Analytics consent" aria-live="polite">
      <p className={styles.text}>
        We use optional analytics (Google Analytics) and, on a small sample of visits, an anonymised
        recording of the pages you see — text and form fields are always masked. Both load only if you
        accept — see our <Link to="/privacy">Privacy Policy</Link>.
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.decline}
          onClick={() => {
            writeConsent('denied')
            setChoice('denied')
          }}
        >
          Decline
        </button>
        <button
          type="button"
          className={styles.accept}
          onClick={() => {
            writeConsent('granted')
            setChoice('granted')
          }}
        >
          Accept
        </button>
      </div>
    </div>
  )
}
